<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import QuickEmailInboxDialog from './components/QuickEmailInboxDialog.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const quickEmailDialogRef = ref(null);

const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const isEmailInbox = inbox => inbox.channel_type === 'Channel::Email';
const isMessengerInbox = inbox => inbox.channel_type === 'Channel::WebWidget';

const getEmailAddress = inbox => inbox.email || inbox.name;

const getEmailDomain = inbox => {
  const email = getEmailAddress(inbox);
  return inbox.sending_domain || email.split('@')[1] || inbox.name;
};

const emailInboxesList = computed(() =>
  filteredInboxesList.value.filter(isEmailInbox)
);

const messengerInboxesList = computed(() =>
  filteredInboxesList.value.filter(isMessengerInbox)
);

const otherInboxesList = computed(() =>
  filteredInboxesList.value.filter(
    inbox => !isEmailInbox(inbox) && !isMessengerInbox(inbox)
  )
);

const getTeamName = inbox =>
  teams.value.find(team => team.id === inbox.default_team_id)?.name ||
  t('INBOX_MGMT.MESSENGER_LIST.UNASSIGNED');

const securityStatus = inbox => ({
  label: inbox.hmac_mandatory
    ? t('INBOX_MGMT.MESSENGER_LIST.SECURE')
    : t('INBOX_MGMT.MESSENGER_LIST.NEEDS_SECURITY'),
  className: inbox.hmac_mandatory
    ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
    : 'bg-n-amber-3 text-n-amber-11 outline-n-amber-5',
});

const emailDomainGroups = computed(() => {
  const grouped = emailInboxesList.value.reduce((acc, inbox) => {
    const domain = getEmailDomain(inbox);
    if (!acc[domain]) acc[domain] = [];
    acc[domain].push(inbox);
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([domain, domainInboxes]) => ({
      domain,
      inboxes: domainInboxes.sort((a, b) =>
        getEmailAddress(a).localeCompare(getEmailAddress(b))
      ),
      authenticated: domainInboxes.some(inbox => inbox.verified_for_sending),
      needsSetup: domainInboxes.some(
        inbox => !inbox.forwarding_confirmed || !inbox.verified_for_sending
      ),
    }))
    .sort((a, b) => a.domain.localeCompare(b.domain));
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

const forwardingStatus = inbox => {
  if (!inbox.forwarding_enabled) {
    return {
      label: t('INBOX_MGMT.EMAIL_LIST.STATUS.OFF'),
      className: 'bg-n-slate-2 text-n-slate-11 outline-n-weak',
      icon: '',
      tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.FORWARDING_OFF'),
    };
  }

  if (inbox.forwarding_confirmed) {
    return {
      label: t('INBOX_MGMT.EMAIL_LIST.STATUS.ON'),
      className: 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5',
      icon: '',
      tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.FORWARDING_ON'),
    };
  }

  if (inbox.cloudflare_provisioning?.status === 'active') {
    return {
      label: t('INBOX_MGMT.EMAIL_LIST.STATUS.TESTING'),
      className: 'bg-n-amber-3 text-n-amber-11 outline-n-amber-5',
      icon: 'i-lucide-loader-circle animate-spin',
      tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.FORWARDING_TESTING'),
    };
  }

  if (inbox.forwarding_test_sent_at) {
    return {
      label: t('INBOX_MGMT.EMAIL_LIST.STATUS.TESTING'),
      className: 'bg-n-amber-3 text-n-amber-11 outline-n-amber-5',
      icon: 'i-lucide-loader-circle animate-spin',
      tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.FORWARDING_TESTING'),
    };
  }

  return {
    label: t('INBOX_MGMT.EMAIL_LIST.STATUS.VERIFY'),
    className: 'bg-n-amber-3 text-n-amber-11 outline-n-amber-5',
    icon: 'i-lucide-circle-alert',
    tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.FORWARDING_VERIFY'),
  };
};

const outboundStatus = inbox => {
  if (inbox.verified_for_sending) {
    return {
      label: t('INBOX_MGMT.EMAIL_LIST.STATUS.ON'),
      className: 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5',
      icon: '',
      tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.OUTBOUND_ON'),
    };
  }

  return {
    label: t('INBOX_MGMT.EMAIL_LIST.STATUS.OFF'),
    className: 'bg-n-slate-2 text-n-slate-11 outline-n-weak',
    icon: '',
    tooltip: t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.OUTBOUND_OFF'),
  };
};

const domainStatusClass = authenticated =>
  authenticated
    ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
    : 'bg-n-amber-3 text-n-amber-11 outline-n-amber-5';

const provisioningAlert = (status, email) => {
  if (status === 'active') {
    return t('INBOX_MGMT.EMAIL_LIST.CREATE.SUCCESS_AUTOMATIC', { email });
  }
  if (status === 'pending_destination_verification') {
    return t('INBOX_MGMT.EMAIL_LIST.CREATE.SUCCESS_PENDING', { email });
  }
  if (status === 'failed') {
    return t('INBOX_MGMT.EMAIL_LIST.CREATE.SUCCESS_FAILED', { email });
  }

  return t('INBOX_MGMT.EMAIL_LIST.CREATE.SUCCESS_MANUAL', { email });
};

const createEmailInbox = async ({ email, teamId }) => {
  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: email,
      default_team_id: teamId,
      auto_configure_email: true,
      channel: { type: 'email', email },
    });
    quickEmailDialogRef.value?.onSuccess();

    const status = inbox.cloudflare_provisioning?.status;
    useAlert(provisioningAlert(status, email));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMAIL_LIST.CREATE.ERROR'));
  }
};

onMounted(() => store.dispatch('teams/get'));
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <div v-if="isAdmin" class="flex items-center gap-2">
            <router-link
              :to="{
                name: 'settings_inboxes_page_channel',
                params: { sub_page: 'website' },
              }"
            >
              <Button
                :label="$t('INBOX_MGMT.EMAIL_LIST.NEW_MESSENGER')"
                icon="i-lucide-message-circle-plus"
                size="sm"
                color="slate"
              />
            </router-link>
            <router-link :to="{ name: 'settings_inbox_new' }">
              <Button
                :label="$t('INBOX_MGMT.EMAIL_LIST.ADD_CHANNEL')"
                size="sm"
                color="slate"
              />
            </router-link>
            <Button
              :label="$t('INBOX_MGMT.EMAIL_LIST.NEW_EMAIL')"
              icon="i-lucide-plus"
              size="sm"
              @click="quickEmailDialogRef?.open()"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="flex flex-col gap-6">
        <section
          class="rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 overflow-hidden"
        >
          <header
            class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-n-weak"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="i-lucide-message-circle size-4 text-n-slate-11 shrink-0"
              />
              <h3 class="text-sm font-semibold text-n-slate-12 mb-0">
                {{ $t('INBOX_MGMT.MESSENGER_LIST.TITLE') }}
              </h3>
              <span class="text-xs text-n-slate-10">
                {{
                  $t('INBOX_MGMT.MESSENGER_LIST.COUNT', {
                    count: messengerInboxesList.length,
                  })
                }}
              </span>
            </div>
            <router-link
              v-if="isAdmin"
              :to="{
                name: 'settings_inboxes_page_channel',
                params: { sub_page: 'website' },
              }"
            >
              <Button
                :label="$t('INBOX_MGMT.EMAIL_LIST.NEW_MESSENGER')"
                icon="i-lucide-plus"
                size="sm"
                color="slate"
              />
            </router-link>
          </header>

          <div
            v-if="messengerInboxesList.length"
            class="hidden lg:grid grid-cols-[minmax(13rem,1.2fr)_minmax(11rem,1fr)_minmax(9rem,0.8fr)_minmax(9rem,0.8fr)_minmax(9rem,0.8fr)_8rem_5rem] gap-3 px-4 py-2 text-xs font-medium text-n-slate-10 border-b border-n-weak"
          >
            <span>{{ $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.NAME') }}</span>
            <span>{{ $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.SITE') }}</span>
            <span>{{ $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.TEAM') }}</span>
            <span>{{ $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.CAPTAIN') }}</span>
            <span>{{
              $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.HELP_CENTER')
            }}</span>
            <span>{{ $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.SECURITY') }}</span>
            <span class="sr-only">{{
              $t('INBOX_MGMT.MESSENGER_LIST.COLUMNS.ACTIONS')
            }}</span>
          </div>

          <div
            v-if="messengerInboxesList.length"
            class="divide-y divide-n-weak"
          >
            <div
              v-for="inbox in messengerInboxesList"
              :key="inbox.id"
              class="grid grid-cols-1 lg:grid-cols-[minmax(13rem,1.2fr)_minmax(11rem,1fr)_minmax(9rem,0.8fr)_minmax(9rem,0.8fr)_minmax(9rem,0.8fr)_8rem_5rem] gap-2 lg:gap-3 px-4 py-3 items-center text-sm"
            >
              <div class="flex items-center gap-3 min-w-0">
                <Avatar
                  :src="inbox.avatar_url"
                  :name="inbox.name"
                  :size="28"
                  rounded-full
                />
                <span class="font-medium text-n-slate-12 truncate">
                  {{ inbox.name }}
                </span>
              </div>
              <span class="text-n-slate-11 truncate">
                {{
                  inbox.website_url || $t('INBOX_MGMT.MESSENGER_LIST.NO_SITE')
                }}
              </span>
              <span class="text-n-slate-11 truncate">{{
                getTeamName(inbox)
              }}</span>
              <span class="text-n-slate-11 truncate">
                {{
                  inbox.captain_assistant?.name ||
                  $t('INBOX_MGMT.MESSENGER_LIST.OFF')
                }}
              </span>
              <span class="text-n-slate-11 truncate">
                {{
                  inbox.help_center?.name ||
                  $t('INBOX_MGMT.MESSENGER_LIST.NONE')
                }}
              </span>
              <span
                class="inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-medium outline outline-1 -outline-offset-1"
                :class="securityStatus(inbox).className"
              >
                {{ securityStatus(inbox).label }}
              </span>
              <router-link
                :to="{
                  name: 'settings_inbox_show',
                  params: { inboxId: inbox.id },
                }"
                class="inline-flex size-8 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              >
                <span class="i-lucide-settings size-4" />
                <span class="sr-only">{{ $t('INBOX_MGMT.SETTINGS') }}</span>
              </router-link>
            </div>
          </div>

          <div
            v-else
            class="flex flex-wrap items-center justify-between gap-3 px-4 py-5"
          >
            <div>
              <p class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('INBOX_MGMT.MESSENGER_LIST.EMPTY_TITLE') }}
              </p>
              <p class="text-sm text-n-slate-10 mb-0">
                {{ $t('INBOX_MGMT.MESSENGER_LIST.EMPTY_DESCRIPTION') }}
              </p>
            </div>
          </div>
        </section>

        <div
          v-if="emailDomainGroups.length"
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div
            class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-n-weak"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span class="i-lucide-mail size-4 text-n-slate-11 shrink-0" />
              <h3 class="text-sm font-semibold text-n-slate-12 mb-0">
                {{ $t('INBOX_MGMT.EMAIL_LIST.TITLE') }}
              </h3>
              <span class="text-xs text-n-slate-10">
                {{
                  $t('INBOX_MGMT.EMAIL_LIST.COUNT', {
                    count: emailInboxesList.length,
                  })
                }}
              </span>
            </div>
            <Button
              v-if="isAdmin"
              :label="$t('INBOX_MGMT.EMAIL_LIST.NEW_EMAIL')"
              icon="i-lucide-plus"
              size="sm"
              color="slate"
              @click="quickEmailDialogRef?.open()"
            />
          </div>

          <div
            v-for="group in emailDomainGroups"
            :key="group.domain"
            class="border-b border-n-weak last:border-b-0"
          >
            <div
              class="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2"
            >
              <div class="flex items-center gap-2 min-w-0">
                <h4
                  class="text-base font-semibold text-n-slate-12 mb-0 truncate"
                >
                  {{ group.domain }}
                </h4>
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium outline outline-1 -outline-offset-1"
                  :class="domainStatusClass(group.authenticated)"
                >
                  {{
                    group.authenticated
                      ? $t('INBOX_MGMT.EMAIL_LIST.STATUS.AUTHENTICATED')
                      : $t('INBOX_MGMT.EMAIL_LIST.STATUS.UNAUTHENTICATED')
                  }}
                </span>
              </div>

              <router-link
                v-if="group.needsSetup && isAdmin"
                :to="{
                  name: 'settings_inbox_show',
                  params: { inboxId: group.inboxes[0].id },
                }"
                class="inline-flex items-center gap-1.5 rounded-lg bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3"
              >
                <span class="i-lucide-arrow-up-right size-3.5" />
                {{ $t('INBOX_MGMT.EMAIL_LIST.ACTIONS.FINISH_SETUP') }}
              </router-link>
            </div>

            <div
              class="hidden md:grid grid-cols-[minmax(15rem,1fr)_12rem_12rem_7rem] gap-4 px-4 py-2 text-xs font-medium text-n-slate-10"
            >
              <span>{{ $t('INBOX_MGMT.EMAIL_LIST.COLUMNS.EMAIL') }}</span>
              <span class="inline-flex items-center gap-1">
                {{ $t('INBOX_MGMT.EMAIL_LIST.COLUMNS.FORWARDING') }}
                <span
                  v-tooltip.top="
                    $t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.FORWARDING')
                  "
                  class="i-lucide-circle-help size-3.5 text-n-slate-9"
                />
              </span>
              <span class="inline-flex items-center gap-1">
                {{ $t('INBOX_MGMT.EMAIL_LIST.COLUMNS.OUTBOUND') }}
                <span
                  v-tooltip.top="$t('INBOX_MGMT.EMAIL_LIST.TOOLTIPS.OUTBOUND')"
                  class="i-lucide-circle-help size-3.5 text-n-slate-9"
                />
              </span>
              <span class="sr-only">
                {{ $t('INBOX_MGMT.EMAIL_LIST.COLUMNS.ACTIONS') }}
              </span>
            </div>

            <div class="divide-y divide-n-weak">
              <div
                v-for="inbox in group.inboxes"
                :key="inbox.id"
                class="grid grid-cols-1 md:grid-cols-[minmax(15rem,1fr)_12rem_12rem_7rem] gap-3 md:gap-4 px-4 py-3 items-center"
              >
                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 mb-0 truncate">
                    {{ getEmailAddress(inbox) }}
                  </p>
                  <p class="md:hidden text-xs text-n-slate-10 mb-0">
                    {{ group.domain }}
                  </p>
                </div>

                <div class="flex items-center justify-between gap-2 md:block">
                  <span class="md:hidden text-xs text-n-slate-10">
                    {{ $t('INBOX_MGMT.EMAIL_LIST.COLUMNS.FORWARDING') }}
                  </span>
                  <span
                    v-tooltip.top="forwardingStatus(inbox).tooltip"
                    class="inline-flex w-fit items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium outline outline-1 -outline-offset-1"
                    :class="forwardingStatus(inbox).className"
                  >
                    <span
                      v-if="forwardingStatus(inbox).icon"
                      class="size-3"
                      :class="forwardingStatus(inbox).icon"
                    />
                    {{ forwardingStatus(inbox).label }}
                  </span>
                </div>

                <div class="flex items-center justify-between gap-2 md:block">
                  <span class="md:hidden text-xs text-n-slate-10">
                    {{ $t('INBOX_MGMT.EMAIL_LIST.COLUMNS.OUTBOUND') }}
                  </span>
                  <span
                    v-tooltip.top="outboundStatus(inbox).tooltip"
                    class="inline-flex w-fit items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium outline outline-1 -outline-offset-1"
                    :class="outboundStatus(inbox).className"
                  >
                    <span
                      v-if="outboundStatus(inbox).icon"
                      class="size-3"
                      :class="outboundStatus(inbox).icon"
                    />
                    {{ outboundStatus(inbox).label }}
                  </span>
                </div>

                <div
                  class="flex items-center justify-start md:justify-end gap-1"
                >
                  <router-link
                    :to="{
                      name: 'settings_inbox_show',
                      params: { inboxId: inbox.id },
                    }"
                    class="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <span class="i-lucide-pencil size-4" />
                    {{ $t('INBOX_MGMT.EMAIL_LIST.ACTIONS.EDIT') }}
                  </router-link>
                  <button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                    type="button"
                    class="inline-flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-ruby-2 hover:text-n-ruby-11"
                    @click="openDelete(inbox)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                    <span class="sr-only">
                      {{ $t('INBOX_MGMT.DELETE.BUTTON_TEXT') }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="otherInboxesList.length"
          class="divide-y divide-n-weak border-t border-n-weak"
        >
          <div
            v-for="inbox in otherInboxesList"
            :key="inbox.id"
            class="flex justify-between flex-row items-start gap-4 py-4"
          >
            <div class="flex items-center gap-4">
              <div
                v-if="inbox.avatar_url"
                class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
              >
                <Avatar
                  :src="inbox.avatar_url"
                  :name="inbox.name"
                  :size="24"
                  rounded-full
                />
              </div>
              <div
                v-else
                class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
              >
                <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
              </div>
              <div class="flex flex-col items-start gap-1">
                <span class="block text-heading-3 text-n-slate-12 capitalize">
                  {{ inbox.name }}
                </span>
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                  :voice-enabled="inbox.voice_enabled"
                  class="text-body-main text-n-slate-11"
                />
              </div>
            </div>
            <div class="flex gap-3 justify-end">
              <router-link
                :to="{
                  name: 'settings_inbox_show',
                  params: { inboxId: inbox.id },
                }"
              >
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                  icon="i-woot-settings"
                  slate
                  sm
                />
              </router-link>
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-woot-bin"
                slate
                sm
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="openDelete(inbox)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
    <QuickEmailInboxDialog
      ref="quickEmailDialogRef"
      :teams="teams"
      :is-loading="uiFlags.isCreating"
      @create="createEmailInbox"
    />
  </SettingsLayout>
</template>
